<script>
import { specialtiesAPI } from '../../../api/specialties';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

export default {
  name: 'SpecialtiesView',
  data() {
    return {
      specialties: [],
      loading: true,
      showModal: false,
      isSaving: false,
      form: this.getEmptyForm(),
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    isEditing() {
      return !!this.form.id;
    },
  },
  mounted() {
    this.fetchSpecialties();
  },
  methods: {
    getEmptyForm() {
      return { id: null, name: '', active: true };
    },
    async fetchSpecialties() {
      this.loading = true;
      try {
        const { data } = await specialtiesAPI.getAll(this.accountId, false);
        this.specialties = data || [];
      } catch {
        useAlert(this.$t('SPECIALTIES.ERRORS.FETCH'));
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.form = this.getEmptyForm();
      this.showModal = true;
    },
    openEditModal(specialty) {
      this.form = {
        id: specialty.id,
        name: specialty.name,
        active: specialty.active,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.form = this.getEmptyForm();
    },
    async saveSpecialty() {
      if (!this.form.name.trim()) {
        useAlert(this.$t('SPECIALTIES.FORM.NAME_REQUIRED'));
        return;
      }
      this.isSaving = true;
      const payload = { name: this.form.name.trim(), active: this.form.active };
      try {
        if (this.isEditing) {
          await specialtiesAPI.update(this.accountId, this.form.id, payload);
          useAlert(this.$t('SPECIALTIES.UPDATED'));
        } else {
          await specialtiesAPI.create(this.accountId, payload);
          useAlert(this.$t('SPECIALTIES.SAVED'));
        }
        this.closeModal();
        await this.fetchSpecialties();
      } catch {
        useAlert(
          this.$t(this.isEditing ? 'SPECIALTIES.ERRORS.UPDATE' : 'SPECIALTIES.ERRORS.SAVE')
        );
      } finally {
        this.isSaving = false;
      }
    },
    async toggleActive(specialty) {
      try {
        await specialtiesAPI.update(this.accountId, specialty.id, {
          name: specialty.name,
          active: !specialty.active,
        });
        await this.fetchSpecialties();
      } catch {
        useAlert(this.$t('SPECIALTIES.ERRORS.UPDATE'));
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full px-6 py-6 overflow-y-auto bg-slate-50">
    <div class="flex items-center justify-between pb-6">
      <h1 class="text-2xl font-bold text-slate-800">{{ $t('SPECIALTIES.TITLE') }}</h1>
      <button
        class="px-4 py-2 font-medium text-white transition-colors rounded-md shadow-sm bg-fluxi-primary hover:bg-fluxi-primary/90"
        @click="openAddModal"
      >
        {{ $t('SPECIALTIES.ADD_BUTTON') }}
      </button>
    </div>

    <p class="mb-4 text-sm text-slate-500">{{ $t('SPECIALTIES.SUBTITLE') }}</p>

    <div class="overflow-x-auto bg-white border rounded-lg shadow-sm border-slate-200">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-slate-50/50">
          <tr>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase text-slate-500">{{ $t('SPECIALTIES.TABLE.NAME') }}</th>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase text-slate-500">{{ $t('SPECIALTIES.TABLE.STATUS') }}</th>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-center uppercase text-slate-500">{{ $t('SPECIALTIES.TABLE.ACTIONS') }}</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr v-if="loading">
            <td colspan="3" class="px-6 py-8 text-sm text-center text-slate-500">{{ $t('SPECIALTIES.TABLE.LOADING') }}</td>
          </tr>
          <tr v-else-if="specialties.length === 0">
            <td colspan="3" class="px-6 py-8 text-sm text-center text-slate-500">{{ $t('SPECIALTIES.TABLE.EMPTY') }}</td>
          </tr>
          <template v-else>
            <tr v-for="specialty in specialties" :key="specialty.id" class="transition-colors hover:bg-slate-50/50">
              <td class="px-6 py-4 text-sm font-medium text-slate-700">{{ specialty.name }}</td>
              <td class="px-6 py-4 text-sm">
                <span
                  class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full"
                  :class="specialty.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'"
                >
                  {{ specialty.active ? $t('SPECIALTIES.TABLE.ACTIVE') : $t('SPECIALTIES.TABLE.INACTIVE') }}
                </span>
              </td>
              <td class="px-6 py-4 space-x-2 text-sm font-medium text-center whitespace-nowrap">
                <button
                  class="inline-flex items-center px-3 py-1 text-xs font-medium transition-colors rounded text-fluxi-primary bg-fluxi-primary/10 hover:bg-fluxi-primary/20"
                  @click="openEditModal(specialty)"
                >
                  {{ $t('SPECIALTIES.TABLE.EDIT') }}
                </button>
                <button
                  class="inline-flex items-center px-3 py-1 text-xs font-medium text-amber-700 transition-colors rounded bg-amber-50 hover:bg-amber-100"
                  @click="toggleActive(specialty)"
                >
                  {{ specialty.active ? $t('SPECIALTIES.TABLE.DEACTIVATE') : $t('SPECIALTIES.TABLE.ACTIVATE') }}
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    >
      <div class="flex flex-col w-full max-w-md bg-white rounded-lg shadow-xl">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 class="text-lg font-bold text-slate-800">
            {{ isEditing ? $t('SPECIALTIES.MODAL.EDIT_TITLE') : $t('SPECIALTIES.MODAL.ADD_TITLE') }}
          </h2>
          <button class="text-xl font-bold text-slate-400 hover:text-slate-600" @click="closeModal">
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="flex-grow px-6 py-4">
          <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('SPECIALTIES.FORM.NAME') }}</label>
          <input
            v-model="form.name"
            type="text"
            class="w-full px-3 py-2 border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
          />
          <label class="flex items-center gap-2 mt-4 text-sm font-medium text-slate-700">
            <input v-model="form.active" type="checkbox" />
            {{ $t('SPECIALTIES.FORM.ACTIVE') }}
          </label>
        </div>

        <div class="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            class="px-4 py-2 font-medium bg-white border rounded text-slate-600 border-slate-300 hover:bg-slate-50"
            @click="closeModal"
          >
            {{ $t('SPECIALTIES.MODAL.CANCEL') }}
          </button>
          <button
            :disabled="isSaving"
            class="px-6 py-2 font-medium text-white rounded bg-fluxi-primary hover:bg-fluxi-primary/90 disabled:opacity-50"
            @click="saveSpecialty"
          >
            {{ isSaving ? $t('SPECIALTIES.MODAL.SAVING') : $t('SPECIALTIES.MODAL.SAVE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
